import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import GavelIcon from '@mui/icons-material/Gavel'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import LockIcon from '@mui/icons-material/Lock'
import ReplayIcon from '@mui/icons-material/Replay'
import SendIcon from '@mui/icons-material/Send'
import {
  disputeService,
  DisputeDto,
  DisputeStatus,
  DisputeResolutionType,
  DisputeResponseType,
  ResolveDisputeRequest,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import ConfirmDialog from '../components/ConfirmDialog'
import { useToast } from '../components/useToast'
import { formatCurrency } from '../utils/formatCurrency'
import { PropertyView, PropertySection, PropertyField } from '../components/PropertyView'
import { fmtDate } from '../utils/propertyFormat'

const STATUS_OPTIONS: DisputeStatus[] = [
  'INITIATED',
  'UNDER_REVIEW',
  'MEDIATION',
  'RESOLVED',
  'CLOSED',
  'ESCALATED',
  'WITHDRAWN',
  'EXPIRED',
]

const RESOLUTION_TYPES: DisputeResolutionType[] = [
  'MUTUAL_AGREEMENT',
  'MEDIATION_DECISION',
  'FULL_REFUND',
  'PARTIAL_REFUND',
  'PAYOUT_ADJUSTMENT',
  'HOLD_PAYMENT',
  'RELEASE_PAYMENT',
  'WITHDRAWN',
]

const RESPONSE_TYPES: DisputeResponseType[] = ['ACKNOWLEDGE', 'ACCEPT', 'ACCEPT_PARTIAL', 'REJECT']

function statusColor(
  s?: string
): 'default' | 'warning' | 'info' | 'success' | 'error' {
  if (s === 'RESOLVED') return 'success'
  if (s === 'CLOSED' || s === 'WITHDRAWN' || s === 'EXPIRED') return 'default'
  if (s === 'ESCALATED') return 'error'
  if (s === 'MEDIATION' || s === 'UNDER_REVIEW') return 'info'
  return 'warning'
}

function isOverdue(d: DisputeDto): boolean {
  if (!d.dueDate) return false
  const open = !['RESOLVED', 'CLOSED', 'WITHDRAWN', 'EXPIRED'].includes(d.status)
  return open && new Date(d.dueDate).getTime() < Date.now()
}

// ----------------- Detail dialog -----------------

function DisputeDetailDialog({
  disputeId,
  open,
  onClose,
}: {
  disputeId: string | null
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  const [statusDraft, setStatusDraft] = useState<DisputeStatus | ''>('')
  const [statusReason, setStatusReason] = useState('')
  const [mediatorId, setMediatorId] = useState('')
  const [responseType, setResponseType] = useState<DisputeResponseType>('ACKNOWLEDGE')
  const [responseComment, setResponseComment] = useState('')

  // Resolve form
  const [resolveOpen, setResolveOpen] = useState(false)
  const [resolutionType, setResolutionType] = useState<DisputeResolutionType>('MEDIATION_DECISION')
  const [ruling, setRuling] = useState('')
  const [awardedAccount, setAwardedAccount] = useState('')
  const [awardedAmount, setAwardedAmount] = useState('')

  // Destructive confirms
  const [closeConfirm, setCloseConfirm] = useState(false)
  const [escalateConfirm, setEscalateConfirm] = useState(false)

  const { data: dispute, isLoading } = useQuery({
    queryKey: ['dispute', disputeId],
    queryFn: () => disputeService.getById(disputeId as string),
    enabled: open && !!disputeId,
  })

  const { data: responses } = useQuery({
    queryKey: ['dispute-responses', disputeId],
    queryFn: () => disputeService.getResponses(disputeId as string),
    enabled: open && !!disputeId,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['dispute', disputeId] })
    queryClient.invalidateQueries({ queryKey: ['dispute-responses', disputeId] })
    queryClient.invalidateQueries({ queryKey: ['disputes-all'] })
    queryClient.invalidateQueries({ queryKey: ['disputes-overdue'] })
    queryClient.invalidateQueries({ queryKey: ['dispute-statistics'] })
  }

  const statusMutation = useMutation({
    mutationFn: () => disputeService.updateStatus(disputeId as string, statusDraft as DisputeStatus, statusReason || undefined),
    onSuccess: () => {
      toast.success('Status updated')
      setStatusDraft('')
      setStatusReason('')
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to update status'),
  })

  const mediatorMutation = useMutation({
    mutationFn: () => disputeService.assignMediator(disputeId as string, mediatorId.trim()),
    onSuccess: () => {
      toast.success('Mediator assigned')
      setMediatorId('')
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to assign mediator'),
  })

  const resolveMutation = useMutation({
    mutationFn: () => {
      const req: ResolveDisputeRequest = {
        resolutionType,
        ruling: ruling.trim(),
        awardedToAccountId: awardedAccount.trim() || undefined,
        awardedAmount: awardedAmount ? parseFloat(awardedAmount) : undefined,
      }
      return disputeService.resolve(disputeId as string, req)
    },
    onSuccess: () => {
      toast.success('Dispute resolved')
      setResolveOpen(false)
      setRuling('')
      setAwardedAccount('')
      setAwardedAmount('')
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to resolve dispute'),
  })

  const escalateMutation = useMutation({
    mutationFn: () => disputeService.escalate(disputeId as string, statusReason || undefined),
    onSuccess: () => {
      toast.success('Dispute escalated')
      setEscalateConfirm(false)
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to escalate'),
  })

  const closeMutation = useMutation({
    mutationFn: () => disputeService.close(disputeId as string),
    onSuccess: () => {
      toast.success('Dispute closed')
      setCloseConfirm(false)
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to close'),
  })

  const reopenMutation = useMutation({
    mutationFn: () => disputeService.reopen(disputeId as string),
    onSuccess: () => {
      toast.success('Dispute reopened')
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reopen'),
  })

  const responseMutation = useMutation({
    mutationFn: () =>
      disputeService.addResponse(disputeId as string, {
        responseType,
        comments: responseComment.trim(),
      }),
    onSuccess: () => {
      toast.success('Response posted')
      setResponseComment('')
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to post response'),
  })

  const isClosed = dispute
    ? ['CLOSED', 'WITHDRAWN', 'EXPIRED'].includes(dispute.status)
    : false

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Dispute Details
        {dispute && (
          <Typography variant="caption" color="text.secondary" display="block">
            {dispute.id}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <TableSkeleton rows={6} columns={2} />}
        {dispute && (
          <Stack spacing={2}>
            {/* Header chips */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={getLocalizedLabel('disputeStatus', dispute.status) || dispute.status}
                color={statusColor(dispute.status)}
                size="small"
              />
              <Chip
                label={getLocalizedLabel('disputeType', dispute.disputeType) || dispute.disputeType}
                size="small"
                variant="outlined"
              />
              {dispute.priority && (
                <Chip label={dispute.priority} size="small" variant="outlined" />
              )}
              {isOverdue(dispute) && <Chip label="Overdue" color="error" size="small" />}
            </Stack>

            {dispute.title && (
              <Typography variant="subtitle1" fontWeight={600}>
                {dispute.title}
              </Typography>
            )}
            {dispute.comments && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {dispute.comments}
              </Typography>
            )}

            <Divider />

            <PropertyView>
              <PropertySection title="Parties">
                <PropertyField label="Initiator account" value={dispute.initiatorAccountId} mono copyable />
                <PropertyField label="Respondent account" value={dispute.respondentAccountId} mono copyable />
                <PropertyField label="Mediator" full>
                  {dispute.mediatorUserId ? (
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {dispute.mediatorUserId}
                      {dispute.mediatorAssignedAt && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {` (assigned ${fmtDate(dispute.mediatorAssignedAt)})`}
                        </Typography>
                      )}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                  )}
                </PropertyField>
                <PropertyField
                  label="Related shipment / job"
                  value={dispute.shipmentId || dispute.jobId}
                  mono
                  copyable
                  full
                />
              </PropertySection>
              <PropertySection title="Amounts & Dates" columns={3}>
                <PropertyField
                  label="Claimed"
                  value={
                    dispute.claimedAmount != null
                      ? `${formatCurrency(dispute.claimedAmount)} ${dispute.currency || ''}`.trim()
                      : null
                  }
                />
                <PropertyField
                  label="Settlement"
                  value={
                    dispute.settlementAmount != null
                      ? `${formatCurrency(dispute.settlementAmount)} ${dispute.currency || ''}`.trim()
                      : null
                  }
                />
                <PropertyField label="Due date">
                  <Typography variant="body2" color={isOverdue(dispute) ? 'error' : 'inherit'}>
                    {fmtDate(dispute.dueDate)}
                  </Typography>
                </PropertyField>
                <PropertyField label="Resolved" value={dispute.resolvedAt} type="date" />
              </PropertySection>
            </PropertyView>

            {dispute.resolutionNotes && (
              <Alert severity="success" variant="outlined">
                <strong>Resolution{dispute.resolutionType ? ` (${dispute.resolutionType})` : ''}:</strong>{' '}
                {dispute.resolutionNotes}
              </Alert>
            )}

            {/* Evidence */}
            {dispute.evidences && dispute.evidences.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2">Evidence ({dispute.evidences.length})</Typography>
                <Stack spacing={0.5}>
                  {dispute.evidences.map((ev) => (
                    <Stack key={ev.id} direction="row" spacing={1} alignItems="center">
                      <Chip label={ev.evidenceType || 'FILE'} size="small" variant="outlined" />
                      <Typography variant="body2">{ev.title || ev.description || ev.fileKey}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}

            {/* Response thread */}
            <Divider />
            <Typography variant="subtitle2">Responses ({responses?.length ?? 0})</Typography>
            {responses && responses.length > 0 ? (
              <Stack spacing={1}>
                {responses.map((r) => (
                  <Paper key={r.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <Chip label={r.responseType || 'RESPONSE'} size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {r.respondedAt ? new Date(r.respondedAt).toLocaleString() : ''}
                        {r.senderAccountId ? ` · ${r.senderAccountId.substring(0, 8)}…` : ''}
                      </Typography>
                    </Stack>
                    {r.comments && <Typography variant="body2">{r.comments}</Typography>}
                    {(r.proposedAmount != null || r.agreedAmount != null) && (
                      <Typography variant="caption" color="text.secondary">
                        {r.proposedAmount != null ? `Proposed ${formatCurrency(r.proposedAmount)} ` : ''}
                        {r.agreedAmount != null ? `Agreed ${formatCurrency(r.agreedAmount)}` : ''}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">No responses yet.</Typography>
            )}

            {!isClosed && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                <TextField
                  select
                  size="small"
                  label="Response type"
                  value={responseType}
                  onChange={(e) => setResponseType(e.target.value as DisputeResponseType)}
                  sx={{ minWidth: 160 }}
                >
                  {RESPONSE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Comment"
                  value={responseComment}
                  onChange={(e) => setResponseComment(e.target.value)}
                  sx={{ flex: 1 }}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  disabled={!responseComment.trim() || responseMutation.isPending}
                  onClick={() => responseMutation.mutate()}
                >
                  Post
                </Button>
              </Stack>
            )}

            <Divider />

            {/* Admin workflow actions */}
            <Typography variant="subtitle2">Admin Actions</Typography>

            {/* Status override */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                select
                size="small"
                label="Set status"
                value={statusDraft}
                onChange={(e) => setStatusDraft(e.target.value as DisputeStatus)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">— select —</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {getLocalizedLabel('disputeStatus', s) || s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Reason"
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                disabled={!statusDraft || statusMutation.isPending}
                onClick={() => statusMutation.mutate()}
              >
                Apply
              </Button>
            </Stack>

            {/* Assign mediator */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                size="small"
                label="Mediator user ID"
                value={mediatorId}
                onChange={(e) => setMediatorId(e.target.value)}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                disabled={!mediatorId.trim() || mediatorMutation.isPending}
                onClick={() => mediatorMutation.mutate()}
              >
                Assign Mediator
              </Button>
            </Stack>

            {/* Action buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {!isClosed && dispute.status !== 'RESOLVED' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<GavelIcon />}
                  onClick={() => setResolveOpen(true)}
                >
                  Resolve…
                </Button>
              )}
              {!isClosed && dispute.status !== 'ESCALATED' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<TrendingUpIcon />}
                  onClick={() => setEscalateConfirm(true)}
                >
                  Escalate…
                </Button>
              )}
              {!isClosed && (
                <Button
                  variant="outlined"
                  startIcon={<LockIcon />}
                  onClick={() => setCloseConfirm(true)}
                >
                  Close…
                </Button>
              )}
              {isClosed && (
                <Button
                  variant="outlined"
                  startIcon={<ReplayIcon />}
                  disabled={reopenMutation.isPending}
                  onClick={() => reopenMutation.mutate()}
                >
                  Reopen
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Resolve dialog */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Dispute</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Resolution type *"
              value={resolutionType}
              onChange={(e) => setResolutionType(e.target.value as DisputeResolutionType)}
              fullWidth
              size="small"
            >
              {RESOLUTION_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Ruling *"
              value={ruling}
              onChange={(e) => setRuling(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
            />
            <TextField
              label="Awarded-to account ID"
              value={awardedAccount}
              onChange={(e) => setAwardedAccount(e.target.value)}
              fullWidth
              size="small"
              helperText="Optional — account receiving the award"
            />
            <TextField
              label="Awarded amount"
              type="number"
              value={awardedAmount}
              onChange={(e) => setAwardedAmount(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">₪</InputAdornment>,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<GavelIcon />}
            disabled={!ruling.trim() || resolveMutation.isPending}
            onClick={() => resolveMutation.mutate()}
          >
            {resolveMutation.isPending ? 'Resolving…' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={escalateConfirm}
        title="Escalate dispute?"
        description="Escalation moves the dispute to senior review and notifies the parties."
        confirmLabel="Escalate"
        severity="warning"
        loading={escalateMutation.isPending}
        onClose={() => setEscalateConfirm(false)}
        onConfirm={() => escalateMutation.mutate()}
      />

      <ConfirmDialog
        open={closeConfirm}
        title="Close dispute?"
        description="Closing finalises the dispute. You can reopen it later if needed."
        confirmPhrase="CLOSE"
        confirmLabel="Close Dispute"
        severity="error"
        loading={closeMutation.isPending}
        onClose={() => setCloseConfirm(false)}
        onConfirm={() => closeMutation.mutate()}
      />
    </Dialog>
  )
}

// ----------------- Page -----------------

export default function DisputesPage() {
  const [page, setPage] = useState(0)
  const [size] = useState(20)
  const [tab, setTab] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const getLocalizedLabel = useLocalizedLabel()
  const queryClient = useQueryClient()

  const allQuery = useQuery({
    queryKey: ['disputes-all', page, size],
    queryFn: () => disputeService.getAll(page, size),
    enabled: tab === 0,
  })
  const overdueQuery = useQuery({
    queryKey: ['disputes-overdue', page, size],
    queryFn: () => disputeService.getOverdue(page, size),
    enabled: tab === 1,
  })
  const { data: statistics } = useQuery({
    queryKey: ['dispute-statistics'],
    queryFn: () => disputeService.getStatistics(),
  })

  const activeQuery = tab === 0 ? allQuery : overdueQuery
  const rows: DisputeDto[] = useMemo(
    () => activeQuery.data?.content ?? [],
    [activeQuery.data]
  )

  const visibleRows = useMemo(() => {
    return rows.filter((d) => {
      if (statusFilter && d.status !== statusFilter) return false
      if (typeFilter && d.disputeType !== typeFilter) return false
      if (search) {
        const q = search.toLowerCase()
        if (
          !d.id.toLowerCase().includes(q) &&
          !(d.title || '').toLowerCase().includes(q) &&
          !(d.initiatorAccountId || '').toLowerCase().includes(q)
        )
          return false
      }
      return true
    })
  }, [rows, statusFilter, typeFilter, search])

  const open = (id: string) => {
    setSelectedId(id)
    setDetailOpen(true)
  }

  const stats = statistics || {}
  const tiles = [
    { label: 'Total', value: stats.totalDisputes ?? '—' },
    { label: 'Open', value: stats.openDisputes ?? '—', color: 'warning.main' },
    { label: 'Overdue', value: stats.overdueDisputes ?? '—', color: 'error.main' },
    {
      label: 'Resolution rate',
      value:
        stats.resolutionRate != null
          ? `${Math.round(Number(stats.resolutionRate) * (Number(stats.resolutionRate) <= 1 ? 100 : 1))}%`
          : stats.resolvedDisputes != null && stats.totalDisputes
          ? `${Math.round((stats.resolvedDisputes / stats.totalDisputes) * 100)}%`
          : '—',
      color: 'success.main',
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={2}>
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => {
              activeQuery.refetch()
              queryClient.invalidateQueries({ queryKey: ['dispute-statistics'] })
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {tiles.map((t) => (
          <Grid item xs={6} sm={3} key={t.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">{t.label}</Typography>
              <Typography variant="h5" fontWeight={700} sx={t.color ? { color: t.color } : undefined}>
                {t.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tab}
        onChange={(_e, v) => {
          setTab(v)
          setPage(0)
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="All Disputes" />
        <Tab label="Overdue" />
      </Tabs>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {getLocalizedLabel('disputeStatus', s) || s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All types</MenuItem>
            {['CARGO_DAMAGE', 'MISSING_ITEMS', 'LATE_DELIVERY', 'PAYOUT_ISSUE'].map((t) => (
              <MenuItem key={t} value={t}>
                {getLocalizedLabel('disputeType', t) || t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search id / title / account"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {activeQuery.isLoading ? (
        <Paper sx={{ flex: 1, minHeight: 0 }}><TableSkeleton rows={8} columns={6} /></Paper>
      ) : activeQuery.isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard
            title="Failed to load disputes"
            message={(activeQuery.error as Error)?.message}
            onRetry={() => activeQuery.refetch()}
          />
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table sx={{ height: visibleRows.length === 0 ? '100%' : undefined }}>
              <TableHead>
                <TableRow>
                  <ResizableTableCell>Title / ID</ResizableTableCell>
                  <ResizableTableCell>Type</ResizableTableCell>
                  <ResizableTableCell>Status</ResizableTableCell>
                  <ResizableTableCell>Initiator</ResizableTableCell>
                  <ResizableTableCell>Due / Claimed</ResizableTableCell>
                  <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                      <EmptyState
                        title="No disputes found"
                        description={
                          tab === 1
                            ? 'There are no overdue disputes right now.'
                            : 'No disputes match the current filters.'
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
                {visibleRows.map((d) => (
                  <TableRow key={d.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(d.id)}>
                    <TableCell>
                      <Typography variant="body2">{d.title || `${d.id.substring(0, 8)}…`}</Typography>
                      {d.title && (
                        <Typography variant="caption" color="text.secondary">
                          {d.id.substring(0, 8)}…
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {getLocalizedLabel('disputeType', d.disputeType) || d.disputeType}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={getLocalizedLabel('disputeStatus', d.status) || d.status}
                          color={statusColor(d.status)}
                          size="small"
                        />
                        {isOverdue(d) && <Chip label="overdue" color="error" size="small" />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {d.initiatorAccountId ? `${d.initiatorAccountId.substring(0, 10)}…` : '—'}
                    </TableCell>
                    <TableCell>
                      {d.dueDate ? new Date(d.dueDate).toLocaleDateString() : '—'}
                      {d.claimedAmount != null && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatCurrency(d.claimedAmount)} {d.currency || ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                      <IconButton size="small" onClick={() => open(d.id)}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {activeQuery.data?.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={activeQuery.data.totalPages}
                page={page + 1}
                onChange={(_e, v) => setPage(v - 1)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <DisputeDetailDialog
        disputeId={selectedId}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false)
          setSelectedId(null)
        }}
      />
    </Box>
  )
}
